import React from "react";
import { CmpProspectFieldsFragment } from "../../../graphql/types";
import { cmpShortDateFormatter } from "../../common/dashboard/formatters";

export type ProspectConversionSummaryProps = Readonly<{
  prospect: CmpProspectFieldsFragment;
}>;

const isValidTimestamp = (
  timestamp: number | null | undefined,
): timestamp is number =>
  typeof timestamp === "number" &&
  Number.isFinite(timestamp) &&
  Number.isFinite(new Date(timestamp).getTime());

export const ProspectConversionSummary = ({
  prospect,
}: ProspectConversionSummaryProps): React.ReactElement => {
  const convertedAt = prospect.convertedAtUtcMilli;
  const createdAt = prospect.createdAtUtcMilli;
  const daysToConvert =
    isValidTimestamp(createdAt) && isValidTimestamp(convertedAt)
      ? Math.max(0, Math.round((convertedAt - createdAt) / 86400000))
      : null;
  const tiles = [
    {
      label: "Prospect Created",
      value: isValidTimestamp(createdAt)
        ? cmpShortDateFormatter.format(createdAt)
        : "-",
    },
    {
      label: "Converted to Member",
      value: isValidTimestamp(convertedAt)
        ? cmpShortDateFormatter.format(convertedAt)
        : "-",
      color: "#10B981",
    },
    {
      label: "Time to Convert",
      value:
        convertedAt === null || convertedAt === undefined
          ? "-"
          : daysToConvert === null
            ? "—"
            : `${daysToConvert} days`,
    },
    { label: "Lead Capture Source", value: prospect.leadCaptureLabel },
  ];

  return (
    <section
      aria-labelledby="prospect-conversion-summary-heading"
      className="flex min-w-0 flex-col gap-4 rounded-xl border border-[#10B981] bg-[#F0FBF7] px-5 py-[22px] md:px-7"
    >
      <div className="flex items-center gap-2.5">
        <svg
          aria-hidden="true"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#10B981"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="shrink-0"
        >
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="m16 11 2 2 4-4" />
        </svg>
        <h2
          id="prospect-conversion-summary-heading"
          className="text-[16px] font-semibold leading-[normal] text-[#0A0A0A]"
        >
          Conversion Summary
        </h2>
      </div>
      <div className="grid grid-cols-1 gap-[14px] md:grid-cols-[repeat(auto-fit,minmax(170px,1fr))]">
        {tiles.map(({ label, value, color }) => (
          <div
            key={label}
            className="min-w-0 rounded-lg border border-[#ECECEC] bg-white px-4 py-3"
          >
            <div className="text-[13px] font-normal leading-[normal] text-[#0A0A0A]">
              {label}
            </div>
            <div
              className="mt-1 break-words text-[15px] font-semibold leading-[normal] text-[#0A0A0A]"
              style={color ? { color } : undefined}
            >
              {value}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default ProspectConversionSummary;
